import { useEffect } from "react";
import {
  Manrope_400Regular,
  Manrope_500Medium,
  Manrope_600SemiBold,
  Manrope_700Bold,
  useFonts as useManropeFonts,
} from "@expo-google-fonts/manrope";
import {
  DMMono_400Regular,
  DMMono_500Medium,
  useFonts as useDMMonoFonts,
} from "@expo-google-fonts/dm-mono";
import { QueryClientProvider } from "@tanstack/react-query";
import { Stack } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { StatusBar } from "expo-status-bar";
import * as Sentry from "@sentry/react-native";
import Toast from "react-native-toast-message";

import { AppLifecycle, useStartupUpdateGate } from "~/lib/app-updates";
import { BackendStatusOverlay } from "~/components/BackendStatusOverlay";
import { MfaGate } from "~/components/composed/MfaGate";
import { QuerySessionBoundary } from "~/components/QuerySessionBoundary";
import { AuthProvider } from "~/hooks/useAuth";
import { queryClient } from "~/lib/query-client";
import { ThemeProvider, useTheme } from "~/styles/theme";
import "~/utils/web-alert";

void SplashScreen.preventAutoHideAsync();

function RootLayout() {
  const [sansLoaded, sansError] = useManropeFonts({
    Manrope_400Regular,
    Manrope_500Medium,
    Manrope_600SemiBold,
    Manrope_700Bold,
  });
  const [monoLoaded, monoError] = useDMMonoFonts({
    DMMono_400Regular,
    DMMono_500Medium,
  });
  const fontsReady =
    (sansLoaded && monoLoaded) || Boolean(sansError || monoError);

  // Hold the splash until fonts load and the launch-time update check finishes (4s cap).
  const updateCheckDone = useStartupUpdateGate();
  const ready = fontsReady && updateCheckDone;

  useEffect(() => {
    if (ready) void SplashScreen.hideAsync();
  }, [ready]);

  if (!ready) return null;

  return (
    <ThemeProvider>
      <AppLifecycle />
      <BackendStatusOverlay />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <MfaGate>
            <QuerySessionBoundary>
              <ThemedStack />
            </QuerySessionBoundary>
          </MfaGate>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

function ThemedStack() {
  const { scheme, colors } = useTheme();
  return (
    <>
      <Stack
        screenOptions={{
          headerShown: false,
          headerStyle: { backgroundColor: colors.primary },
          contentStyle: {
            backgroundColor: colors.background,
          },
        }}
      />
      <StatusBar style={scheme === "dark" ? "light" : "dark"} />
      <Toast />
    </>
  );
}

export default Sentry.wrap(RootLayout);
